import { useMemo } from 'react';

import { MapData } from '@atom/types/map';

import AssetGroupMarker from './AssetGroupMarker';
import AssetSingleMarker from './AssetSingleMarker';
import ClusterMarker from './ClusterMarker';
import { groupIdOf, partitionByLocation, stackKeyOf } from './markerGrouping';
import {
  isGroupAllDisabled,
  isIdDisabled,
  resolveIsActive,
} from './markerUtilities';
import TaskLocationMarker from './TaskLocationMarker';
import UserMarker from './UserMarker';
import WorkGroupMarker from './WorkGroupMarker';
import WorkSingleMarker from './WorkSingleMarker';

const EMPTY_DISABLED_IDS: ReadonlySet<string> = new Set();
const noop = () => {};

type MarkerLayerProps = {
  data: MapData;
  activeId?: string;
  activeStack?: string;
  hoverId?: string;
  disabledIds?: ReadonlySet<string>;
  onAssetGroupClick?: (stackKey: string) => void;
  onWorkOrderGroupClick?: (stackKey: string) => void;
  onMarkerClick?: (id: string, view: string) => void;
};

const MarkerLayer = ({
  data,
  activeId = '',
  activeStack = '',
  hoverId = '',
  disabledIds = EMPTY_DISABLED_IDS,
  onAssetGroupClick = noop,
  onWorkOrderGroupClick = noop,
  onMarkerClick = noop,
}: MarkerLayerProps) => {
  const {
    clusters = [],
    assets = [],
    workOrders = [],
    users = [],
    locations = [],
  } = data;

  const assetPartition = useMemo(() => partitionByLocation(assets), [assets]);
  const workOrderPartition = useMemo(
    () => partitionByLocation(workOrders),
    [workOrders],
  );

  return (
    <>
      {clusters.map(cluster => (
        <ClusterMarker key={cluster.id} cluster={cluster} />
      ))}
      {assetPartition.singles.map(asset => (
        <AssetSingleMarker
          key={asset.id}
          asset={asset}
          isActive={resolveIsActive(asset.id, activeId, hoverId)}
          disabled={isIdDisabled(asset.id, disabledIds)}
          onMarkerClick={onMarkerClick}
        />
      ))}
      {assetPartition.groups.map(group => {
        const ids = groupIdOf(group);
        const stackKey = stackKeyOf(group);

        return (
          <AssetGroupMarker
            key={ids}
            group={group}
            isActive={
              stackKey === activeStack ||
              resolveIsActive(ids, activeId, hoverId)
            }
            allDisabled={isGroupAllDisabled(ids, disabledIds)}
            stackKey={stackKey}
            onAssetGroupClick={onAssetGroupClick}
          />
        );
      })}
      {workOrderPartition.singles.map(workOrder => (
        <WorkSingleMarker
          key={workOrder.id}
          workOrder={workOrder}
          isActive={resolveIsActive(workOrder.id, activeId, hoverId)}
          disabled={isIdDisabled(workOrder.id, disabledIds)}
          onMarkerClick={onMarkerClick}
        />
      ))}
      {workOrderPartition.groups.map(group => {
        const ids = groupIdOf(group);
        const stackKey = stackKeyOf(group);

        return (
          <WorkGroupMarker
            key={ids}
            group={group}
            isActive={
              stackKey === activeStack ||
              resolveIsActive(ids, activeId, hoverId)
            }
            allDisabled={isGroupAllDisabled(ids, disabledIds)}
            stackKey={stackKey}
            onWorkOrderGroupClick={onWorkOrderGroupClick}
          />
        );
      })}
      {users.map(user => (
        <UserMarker
          key={user.id}
          user={user}
          isActive={resolveIsActive(user.id, activeId, hoverId)}
          disabled={isIdDisabled(user.id, disabledIds)}
          onMarkerClick={onMarkerClick}
        />
      ))}
      {locations.map(location => (
        <TaskLocationMarker key={location.id} location={location} />
      ))}
    </>
  );
};

export default MarkerLayer;
